import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button, Input, Spinner } from "@/components/ui/Primitives";
import { LogIn, UserPlus } from "lucide-react";
import { formatErr } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1621072789072-f7023b85a924?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxvbmlvbiUyMGZhcm0lMjBzdW5zZXR8ZW58MHx8fHwxNzc4MTIyNTM2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const swapMode = (m) => { setMode(m); setError(""); setSuccess(""); setPassword2(""); };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSuccess("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (password.length < 4) { setError("A senha precisa ter pelo menos 4 caracteres."); return; }
    if (password !== password2) { setError("As senhas não coincidem."); return; }
    setBusy(true); setError(""); setSuccess("");
    try {
      const res = await register(email, password, name);
      setSuccess(res?.message || "Cadastro enviado. Aguarde a liberação do administrador antes de fazer login.");
      // Reset o form e volta pra aba de login (usuário não pode entrar ainda)
      setName(""); setPassword(""); setPassword2("");
      setTimeout(() => setMode("login"), 3500);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F5F2ED]">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-1 items-center justify-center p-12 overflow-hidden">
        <img src={BG} alt="Plantação ao entardecer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(15, 40, 30, 0.65)" }} />
        <div className="relative z-10 text-center max-w-md">
          <h1 className="text-white font-heading font-black text-[56px] leading-none tracking-[4px]">CEBOLAS</h1>
          <h2 className="font-heading font-extrabold text-[28px] tracking-[8px] mt-2" style={{ color: "#C9A24E" }}>SERENATA</h2>
          <div className="w-20 h-[3px] mx-auto my-6" style={{ background: "#C9A24E" }} />
          <p className="text-white/85 text-[18px] leading-[28px] font-light">
            Sistema de gestão
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 w-full lg:w-[520px] lg:max-w-[45%]">
        <div className="bg-white rounded-lg p-9 w-full max-w-[420px] shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
          <div className="lg:hidden text-center mb-6">
            <div className="font-heading font-black text-[28px] tracking-[3px] text-brand-500">CEBOLAS</div>
            <div className="font-heading font-extrabold text-[14px] tracking-[6px]" style={{ color: "#C9A24E" }}>SERENATA</div>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-stone-100 rounded-md mb-6">
            <button
              type="button"
              onClick={() => swapMode("login")}
              data-testid="tab-login"
              className={`text-sm font-semibold py-2 rounded-md transition-colors ${
                mode === "login" ? "bg-white text-brand-500 shadow-sm" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => swapMode("register")}
              data-testid="tab-register"
              className={`text-sm font-semibold py-2 rounded-md transition-colors ${
                mode === "register" ? "bg-white text-brand-500 shadow-sm" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              Criar conta
            </button>
          </div>

          {mode === "login" ? (
            <>
              <h1 className="font-heading text-[26px] font-bold text-ink-900">Bem-vindo</h1>
              <p className="text-sm text-ink-500 mt-1 mb-6">Acesse sua conta para continuar.</p>

              <form onSubmit={submitLogin} className="space-y-4" data-testid="login-form">
                <Input
                  label="Email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
                <Input
                  label="Senha" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
                {error && (
                  <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="login-submit-button">
                  {busy ? <Spinner size={18} /> : <><LogIn size={18} /> ENTRAR</>}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-heading text-[26px] font-bold text-ink-900">Criar sua conta</h1>
              <p className="text-sm text-ink-500 mt-1 mb-6">Preencha os dados abaixo. Um administrador liberará suas permissões após o cadastro.</p>

              <form onSubmit={submitRegister} className="space-y-4" data-testid="register-form">
                <Input
                  label="Nome completo" type="text" required autoComplete="name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                />
                <Input
                  label="Email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email-input"
                />
                <Input
                  label="Senha" type="password" required autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="register-password-input"
                />
                <Input
                  label="Confirme a senha" type="password" required autoComplete="new-password"
                  value={password2} onChange={(e) => setPassword2(e.target.value)}
                  data-testid="register-password2-input"
                />
                {error && (
                  <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="register-error">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="text-sm text-ok bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md" data-testid="register-success">
                    {success}
                  </div>
                )}
                <Button type="submit" size="lg" className="w-full" disabled={busy || !!success} data-testid="register-submit-button">
                  {busy ? <Spinner size={18} /> : <><UserPlus size={18} /> CRIAR CONTA</>}
                </Button>
              </form>
            </>
          )}

          <div className="mt-8 pt-5 border-t border-tertiary text-center">
            <div className="text-[11px] text-ink-300">© Cebolas Serenata Ltda</div>
          </div>
        </div>
      </div>
    </div>
  );
}
